import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '#environments';
import { AssetChangeEntry, AssetDetail, AssetSummary, AssetTypeCounts, CreateAsset, HierarchyFacetNode, PaginatedResult, UpdateAsset } from '#models';

export interface AssetFilters {
    vertical?: string;
    subvertical?: string;
    hierarchyId?: number;
    assetType?: string;
    name?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortOrder?: 1 | -1;
}

@Injectable({ providedIn: 'root' })
export class AssetService {
    private _http = inject(HttpClient);
    private _base = `${environment.apiBaseUrl}/v1/assets`;

    getAssets(filters: AssetFilters = {}): Observable<PaginatedResult<AssetSummary>> {
        const params: Record<string, string> = {};
        if (filters.vertical) params['vertical'] = filters.vertical;
        if (filters.subvertical) params['subvertical'] = filters.subvertical;
        if (filters.hierarchyId) params['hierarchyId'] = String(filters.hierarchyId);
        if (filters.assetType) params['assetType'] = filters.assetType;
        if (filters.name) params['name'] = filters.name;
        if (filters.search) params['search'] = filters.search;
        if (filters.page != null) params['page'] = String(filters.page);
        if (filters.pageSize != null) params['pageSize'] = String(filters.pageSize);
        if (filters.sortField) params['sortField'] = filters.sortField;
        if (filters.sortOrder != null) params['sortOrder'] = filters.sortOrder === -1 ? 'desc' : 'asc';
        return this._http.get<PaginatedResult<AssetSummary>>(this._base, { params });
    }

    getAssetTypeCounts(filters: Pick<AssetFilters, 'vertical' | 'subvertical' | 'hierarchyId' | 'search'> = {}): Observable<AssetTypeCounts> {
        const params: Record<string, string> = {};
        if (filters.vertical) params['vertical'] = filters.vertical;
        if (filters.subvertical) params['subvertical'] = filters.subvertical;
        if (filters.hierarchyId) params['hierarchyId'] = String(filters.hierarchyId);
        if (filters.search) params['search'] = filters.search;
        return this._http.get<AssetTypeCounts>(`${this._base}/type-counts`, { params });
    }

    /** Returns the direct count of assets per hierarchy node, with no search or type filter.
     *  Used to populate drill-down panel counts independently of the grid search term. */
    getHierarchyNodeCounts(): Observable<Record<string, number>> {
        return this._http
            .get<{ nodeCounts: Record<string, number> }>(`${this._base}/hierarchy-node-counts`)
            .pipe(map(r => r.nodeCounts));
    }

    /**
     * Returns hierarchy nodes derived from the user's authorized asset data, filtered
     * by the active search and assetType context. Only nodes with at least one visible
     * asset are included; ancestor nodes for tree rendering are included with count = 0.
     * The server enforces data isolation via EF Core global query filters.
     */
    getHierarchyFacets(filters: { search?: string; assetType?: string } = {}): Observable<HierarchyFacetNode[]> {
        const params: Record<string, string> = {};
        if (filters.search) params['search'] = filters.search;
        if (filters.assetType) params['assetType'] = filters.assetType;
        return this._http
            .get<{ nodes: HierarchyFacetNode[] }>(`${this._base}/hierarchy-facets`, { params })
            .pipe(map(r => r.nodes));
    }

    getAssetById(assetId: string): Observable<AssetDetail> {
        return this._http.get<AssetDetail>(`${this._base}/${assetId}`);
    }

    getRetirementData(assetId: string): Observable<AssetDetail> {
        return this._http.get<AssetDetail>(`${this._base}/${assetId}/retirement-data`);
    }

    createAsset(dto: CreateAsset): Observable<AssetDetail> {
        return this._http.post<AssetDetail>(this._base, dto);
    }

    updateAsset(assetId: string, dto: UpdateAsset): Observable<AssetDetail> {
        return this._http.put<AssetDetail>(`${this._base}/${assetId}`, dto);
    }

    deleteAsset(assetId: string): Observable<void> {
        return this._http.delete<void>(`${this._base}/${assetId}`);
    }

    getAssetHistory(assetId: string): Observable<AssetChangeEntry[]> {
        return this._http.get<AssetChangeEntry[]>(`${this._base}/${assetId}/history`);
    }
}
